/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Fragment, MouseEvent, useMemo, useState } from 'react'
import { Box, Grid, IconButton, Popover, SelectChangeEvent, Stack, Tooltip, Typography } from '@mui/material'
import { UseFiltersInstanceProps } from 'react-table'
import { nanoid } from 'nanoid'
import ClearAll from '@mui/icons-material/ClearAll'
import Close from '@mui/icons-material/Close'
import FilterList from '@mui/icons-material/FilterList'
import { EnhancedHeader } from '../../../types'
import { SimpleMap } from '@vendorflow/types'
import { MobilePopoverStyling } from '../TablePieces/Toolbar'
import { InputSearchDropdown } from '../..'

interface Props<D extends object> {
  allColumns: EnhancedHeader<D>[]
  columnNameById: SimpleMap<string>
  setAllFilters: UseFiltersInstanceProps<D>['setAllFilters']
}

export default function FilterTool<D extends object>({ allColumns, columnNameById, setAllFilters }: Props<D>) {
  const popoverId = useMemo(() => nanoid(), [])
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [filter, setFilter] = useState<string | null>(null)
  const open = !!anchorEl
  const { columnById, options }: { columnById: SimpleMap<EnhancedHeader<D>>; options: string[] } = useMemo(() => {
    const columnById: SimpleMap<EnhancedHeader<D>> = {}
    const options: string[] = []
    allColumns.forEach((col) => {
      if (col.canFilter) {
        columnById[col.id] = col
        options.push(col.id)
      }
    })

    return { columnById, options }
  }, [allColumns])

  function handleClick(evt: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(evt.currentTarget)
  }

  function handleClose() {
    setAnchorEl(null)
    setFilter(null)
  }

  return (
    <Fragment>
      <Tooltip title="Filter Table">
        <IconButton aria-describedby={popoverId} onClick={handleClick}>
          <FilterList />
        </IconButton>
      </Tooltip>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box padding="1rem" width="20rem" boxSizing="border-box" css={MobilePopoverStyling}>
          <Grid container spacing={1}>
            <Grid item md={12} sm={12} xs={12}>
              <Stack sx={{ marginBottom: '0.5rem' }} direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  css={css`
                    font-size: 14px;
                    color: rgba(0, 0, 0, 0.54);
                  `}
                >
                  Filters
                </Typography>
                <div>
                  <Tooltip title="Clear All Filters">
                    <IconButton onClick={() => setAllFilters([])}>
                      <ClearAll />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close">
                    <IconButton onClick={handleClose}>
                      <Close />
                    </IconButton>
                  </Tooltip>
                </div>
              </Stack>
            </Grid>
            <Grid marginBottom="1rem" item md={12} sm={12} xs={12}>
              <InputSearchDropdown
                label="Column"
                size="small"
                fullWidth
                value={filter}
                onChange={(evt: SelectChangeEvent) => setFilter(evt.target.value)}
                options={options}
                getOptionLabel={(option) => columnNameById[option]}
              />
            </Grid>
            {filter && columnById[filter].canFilter && (
              <Grid item md={12} sm={12} xs={12}>
                {/** @ts-ignore */}
                {columnById[filter].render('Filter')}
              </Grid>
            )}
          </Grid>
        </Box>
      </Popover>
    </Fragment>
  )
}
